import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, matchPath } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import UnitsPage from "./pages/UnitsPage";
import PropertiesPage from "./pages/PropertiesPage";
import LoginPage from "./pages/LoginPage";
import PasswordResetPage from "./pages/PasswordResetPage";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import FinanceProtectedRoute from "./components/auth/FinanceProtectedRoute";
import UnitDetailsPage from "./pages/UnitDetailsPage";
import PropertyDetailsPage from "./pages/PropertyDetailsPage";
import PropertyUserAccessPage from "./pages/PropertyUserAccessPage";
import PropertyMetadataPage from "./pages/PropertyMetadataPage";
import PropertyFME1MetadataPage from "./pages/PropertyFME1MetadataPage";
import AdminUserManagement from "./pages/AdminUserManagement";
import CreatePropertyPage from "./pages/CreatePropertyPage";
import EditProperty from "./pages/EditProperty";
import InsuranceFormPage from "./pages/insurance/InsuranceFormPage";
import InsuranceListPage from "./pages/insurance/InsuranceListPage";
import UtilityFormPage from "./pages/utilities/UtilityFormPage";
import UtilityListPage from "./pages/utilities/UtilityListPage";
import AssessmentFormPage from "./pages/assessment/AssessmentFormPage";
import AssessmentListPage from "./pages/assessment/AssessmentListPage";
import UserAssignmentsPage from "./pages/UserAssignmentsPage";
import LPE1Form from "./pages/LPE1Form";
import FME1Form from "./pages/FME1Form";
import CreateUnitPage from "./pages/CreateUnitPage";
import EditUnitPage from "./pages/EditUnitPage";
import SupplierDetailsPage from "./pages/SupplierDetailsPage";
import SuppliersPage from "./pages/SuppliersPage";
import TicketsPage from "./pages/TicketsPage";
import RaiseTicketPage from "./pages/RaiseTicketPage";
import ProfilePage from "./pages/ProfilePage";
import RenewalsPage from "./pages/RenewalsPage";
import DocumentsPage from "./pages/DocumentsPage";
import PortalWideDefaultsPage from "./pages/PortalWideDefaultsPage";
import SettingsPage from "./pages/SettingsPage";
import SearchPage from "./pages/SearchPage";
import FinanceDashboardPage from "./pages/finance/FinanceDashboardPage";
import UnitStatementPage from "./pages/finance/UnitStatementPage";
import UnitTransfersPage from "./pages/UnitTransfersPage";
import SalesPage from "./pages/SalesPage";
import SaleDetailsPage from "./pages/SaleDetailsPage";
import SessionAlerts from "./components/auth/SessionAlerts";
import { SYSTEM_NAME } from "./config";

const PAGE_TITLES = [
  { path: "/login", title: "Sign in" },
  { path: "/login/reset", title: "Reset password" },
  { path: "/profile", title: "Profile" },
  { path: "/dashboard", title: "Dashboard" },
  { path: "/properties", title: "Properties" },
  { path: "/properties/create", title: "Create Property" },
  { path: "/properties/:propertyId/edit", title: "Edit Property" },
  { path: "/properties/:propertyId/details", title: "Property Details" },
  { path: "/properties/:propertyId/users/:userId", title: "User access" },
  { path: "/properties/:propertyId/units", title: "Units" },
  { path: "/properties/:propertyId/units/create", title: "Create Unit" },
  { path: "/properties/:propertyId/metadata/lpe1", title: "Property Metadata" },
  { path: "/properties/:propertyId/metadata/fme1", title: "FME1 Metadata" },
  { path: "/properties/:propertyId/insurance", title: "Insurance" },
  { path: "/properties/:propertyId/insurance/create", title: "Add Insurance" },
  { path: "/properties/:propertyId/insurance/:insuranceId/edit", title: "Edit Insurance" },
  { path: "/properties/:propertyId/utilities", title: "Utilities" },
  { path: "/properties/:propertyId/utilities/create", title: "Add Utility" },
  { path: "/properties/:propertyId/utilities/:utilityId/edit", title: "Edit Utility" },
  { path: "/properties/:propertyId/assessments", title: "Assessments" },
  { path: "/properties/:propertyId/assessments/create", title: "Add Assessment" },
  { path: "/properties/:propertyId/assessments/:assessmentId/edit", title: "Edit Assessment" },
  { path: "/units/:unitId/details", title: "Unit Details" },
  { path: "/units/:unitId/edit", title: "Edit Unit" },
  { path: "/units/:unitId/insurance", title: "Insurance" },
  { path: "/units/:unitId/insurance/create", title: "Add Insurance" },
  { path: "/units/:unitId/insurance/:insuranceId/edit", title: "Edit Insurance" },
  { path: "/units/:unitId/utilities", title: "Utilities" },
  { path: "/units/:unitId/utilities/create", title: "Add Utility" },
  { path: "/units/:unitId/utilities/:utilityId/edit", title: "Edit Utility" },
  { path: "/units/:unitId/assessments", title: "Assessments" },
  { path: "/units/:unitId/assessments/create", title: "Add Assessment" },
  { path: "/units/:unitId/assessments/:assessmentId/edit", title: "Edit Assessment" },
  { path: "/admin/users", title: "User Management" },
  { path: "/admin/users/:userId/assignments", title: "User Assignments" },
  { path: "/suppliers", title: "Suppliers" },
  { path: "/tickets", title: "Tickets" },
  { path: "/tickets/new", title: "Raise a ticket" },
  { path: "/renewals", title: "Renewals" },
  { path: "/documents", title: "Documents" },
  { path: "/settings", title: "Settings" },
  { path: "/finance", title: "Finance" },
  { path: "/units/:unitId/statement", title: "Owner Statement" },
  { path: "/search", title: "Search" },
  { path: "/unit-transfers", title: "Unit Transfers" },
  { path: "/sales", title: "Sales" },
  { path: "/sales/:saleId", title: "Sale" },
  { path: "/portal-defaults/lpe1", title: "Portal Defaults" },
  { path: "/lpe1/new", title: "New LPE1 Questionnaire" },
  { path: "/lpe1/:lpe1Id", title: "LPE1 Questionnaire" },
  { path: "/fme1/new", title: "New FME1 Questionnaire" },
  { path: "/fme1/:fme1Id", title: "FME1 Questionnaire" },
];

function PageTitleManager() {
  const location = useLocation();

  useEffect(() => {
    const match = PAGE_TITLES.find(({ path }) =>
      matchPath({ path, end: true }, location.pathname)
    );
    document.title = match ? `${match.title} | ${SYSTEM_NAME}` : SYSTEM_NAME;
  }, [location.pathname]);

  return null;
}

function RedirectPropertyFinance() {
  const { propertyId } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("propertyId", propertyId);
  const tab = params.get("tab");
  if (tab !== "purchase-orders" && tab !== "supplier-bills") params.delete("tab");
  const query = params.toString();
  return <Navigate to={query ? `/finance?${query}` : "/finance"} replace />;
}

function App() {
  return (
    <Router>
        <PageTitleManager />
        <SessionAlerts />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/reset" element={<PasswordResetPage />} />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/properties/:propertyId/units"
            element={
              <ProtectedRoute>
                <UnitsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/properties"
            element={
              <ProtectedRoute>
                <PropertiesPage />
              </ProtectedRoute>
            }
          />
          <Route path="/unit-transfers" element={<ProtectedRoute><UnitTransfersPage /></ProtectedRoute>} />

          <Route
            path="/units/:unitId/details"
            element={
              <ProtectedRoute>
                <UnitDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/units/:unitId/edit"
            element={
              <ProtectedRoute>
                <EditUnitPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/properties/:propertyId/details"
            element={
              <ProtectedRoute>
                <PropertyDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties/:propertyId/users/:userId"
            element={
              <ProtectedRoute>
                <PropertyUserAccessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties/:propertyId/metadata/lpe1"
            element={
              <ProtectedRoute>
                <PropertyMetadataPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties/:propertyId/metadata/fme1"
            element={
              <ProtectedRoute>
                <PropertyFME1MetadataPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <AdminUserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers/:supplierId"
            element={<ProtectedRoute><SupplierDetailsPage /></ProtectedRoute>}
          />
          <Route
            path="/suppliers"
            element={<ProtectedRoute><SuppliersPage /></ProtectedRoute>}
          />
          <Route
            path="/sales/:saleId"
            element={<ProtectedRoute><SaleDetailsPage /></ProtectedRoute>}
          />
          <Route
            path="/sales"
            element={<ProtectedRoute><SalesPage /></ProtectedRoute>}
          />
          <Route
            path="/tickets"
            element={
              <ProtectedRoute>
                <TicketsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets/new"
            element={
              <ProtectedRoute>
                <RaiseTicketPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/renewals"
            element={
              <ProtectedRoute>
                <RenewalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <DocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <SearchPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal-defaults/lpe1"
            element={
              <ProtectedRoute>
                <PortalWideDefaultsPage />
              </ProtectedRoute>
            }
          />

        <Route
          path="/properties/create"
          element={
            <ProtectedRoute>
              <CreatePropertyPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/properties/:propertyId/edit"
          element={
            <ProtectedRoute>
              <EditProperty />
            </ProtectedRoute>
          }
        />

          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/login" replace />} />

        <Route
          path="/properties/:propertyId/insurance"
          element={
            <ProtectedRoute>
              <InsuranceListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/properties/:propertyId/insurance/create"
          element={
            <ProtectedRoute>
              <InsuranceFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/properties/:propertyId/insurance/:insuranceId/edit"
          element={
            <ProtectedRoute>
              <InsuranceFormPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/units/:unitId/insurance"
          element={
            <ProtectedRoute>
              <InsuranceListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/units/:unitId/insurance/create"
          element={
            <ProtectedRoute>
              <InsuranceFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/units/:unitId/insurance/:insuranceId/edit"
          element={
            <ProtectedRoute>
              <InsuranceFormPage />
            </ProtectedRoute>
          }
        />


        {/* Property-level utilities */}
        <Route
          path="/properties/:propertyId/utilities"
          element={
            <ProtectedRoute>
              <UtilityListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/properties/:propertyId/utilities/create"
          element={
            <ProtectedRoute>
              <UtilityFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/properties/:propertyId/utilities/:utilityId/edit"
          element={
            <ProtectedRoute>
              <UtilityFormPage />
            </ProtectedRoute>
          }
        />

        {/* Unit-level utilities */}
        <Route
          path="/units/:unitId/utilities"
          element={
            <ProtectedRoute>
              <UtilityListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/units/:unitId/utilities/create"
          element={
            <ProtectedRoute>
              <UtilityFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/units/:unitId/utilities/:utilityId/edit"
          element={
            <ProtectedRoute>
              <UtilityFormPage />
            </ProtectedRoute>
          }
        />


        {/* PROPERTY-level assessments */}
        <Route
          path="/properties/:propertyId/assessments"
          element={
            <ProtectedRoute>
              <AssessmentListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/properties/:propertyId/assessments/create"
          element={
            <ProtectedRoute>
              <AssessmentFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/properties/:propertyId/assessments/:assessmentId/edit"
          element={
            <ProtectedRoute>
              <AssessmentFormPage />
            </ProtectedRoute>
          }
        />

        {/* UNIT-level assessments */}
        <Route
          path="/units/:unitId/assessments"
          element={
            <ProtectedRoute>
              <AssessmentListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/units/:unitId/assessments/create"
          element={
            <ProtectedRoute>
              <AssessmentFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/units/:unitId/assessments/:assessmentId/edit"
          element={
            <ProtectedRoute>
              <AssessmentFormPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users/:userId/assignments"
          element={
            <ProtectedRoute>
              <UserAssignmentsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/lpe1/new"
          element={
            <ProtectedRoute>
              <LPE1Form />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lpe1/:lpe1Id"
          element={
            <ProtectedRoute>
              <LPE1Form />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fme1/new"
          element={
            <ProtectedRoute>
              <FME1Form />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fme1/:fme1Id"
          element={
            <ProtectedRoute>
              <FME1Form />
            </ProtectedRoute>
          }
        />

        <Route
          path="/properties/:propertyId/units/create"
          element={
            <ProtectedRoute>
              <CreateUnitPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/finance"
          element={
            <ProtectedRoute>
              <FinanceProtectedRoute>
                <FinanceDashboardPage />
              </FinanceProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/properties/:propertyId/finance"
          element={
            <ProtectedRoute>
              <FinanceProtectedRoute>
                <RedirectPropertyFinance />
              </FinanceProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/units/:unitId/statement"
          element={
            <ProtectedRoute>
              <FinanceProtectedRoute>
                <UnitStatementPage />
              </FinanceProtectedRoute>
            </ProtectedRoute>
          }
        />

        </Routes>
    </Router>
  );
}

export default App;
